import { Button, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import { MediaControlSettingsForm } from '@/components/playout/media-control-settings-form';
import type { ResolvedMediaControlSettings } from '@/types/playout/media-control-settings.types';
import { usePatchOrganizationMediaControlSettingsMutation } from '@/state/mutations/playout/use-patch-media-control-settings';
import { useNotifications } from '@/hooks/use-notifications';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';

export function OrganizationPlayoutSettingsPanel({ organizationId }: { organizationId?: string }) {
  const t = useTranslations();
  const notification = useNotifications();
  const { getToken } = useAuth();
  const { mutateAsync, isPending } = usePatchOrganizationMediaControlSettingsMutation(organizationId);
  const [settings, setSettings] = useState<ResolvedMediaControlSettings | null>(null);

  useEffect(() => {
    if (!organizationId) return;

    let cancelled = false;

    void (async () => {
      try {
        const token = await getToken();
        const url = `${import.meta.env.VITE_API_URL}/organizations/${organizationId}/playout/media-control`;
        const data = (await sendJSONRequest({
          url,
          method: METHOD.GET,
          token: token ?? '',
        })) as ResolvedMediaControlSettings;
        if (!cancelled) {
          setSettings(data);
        }
      } catch {
        if (!cancelled) {
          notification.notifyError(
            t('playout.mediaControl.loadError', { defaultValue: 'Could not load media control settings' }),
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId, getToken, notification, t]);

  if (!settings) {
    return null;
  }

  const handleSave = async () => {
    try {
      const updated = await mutateAsync({
        mediaControl: {
          bindings: settings.bindings,
          allowEventOverride: settings.allowEventOverride,
        },
      });
      setSettings(updated);
      notification.notifySuccess(
        t('playout.mediaControl.saveSuccess', { defaultValue: 'Media control settings saved' }),
      );
    } catch {
      notification.notifyError(
        t('playout.mediaControl.saveError', { defaultValue: 'Could not save media control settings' }),
      );
    }
  };

  return (
    <Stack spacing={2}>
      <MediaControlSettingsForm
        settings={settings}
        disabled={isPending}
        showAllowEventOverride
        onBindingsChange={(bindings) => setSettings((prev) => (prev ? { ...prev, bindings } : prev))}
        onAllowEventOverrideChange={(allowEventOverride) =>
          setSettings((prev) => (prev ? { ...prev, allowEventOverride } : prev))
        }
      />
      <Button variant="contained" onClick={() => void handleSave()} disabled={isPending}>
        {t('common.save')}
      </Button>
    </Stack>
  );
}
